import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../context/AuthContext";
import { Clapperboard, CalendarDays, MapPin, ArrowRight } from "lucide-react";

const STATUS_LABEL = { new: "New project", in_production: "In production" };

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export const LandingHighlights = () => {
  const [projects, setProjects] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pr, fr] = await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/festivals`),
        ]);
        setProjects((pr.data || []).filter((p) => p.status === "new" || p.status === "in_production").slice(0, 3));
        const now = new Date();
        const upcoming = (fr.data || [])
          .filter((f) => f.start_datetime && new Date(f.start_datetime) >= now)
          .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        setNextEvent(upcoming[0] || null);
      } catch { /* strips simply don't render */ }
    })();
  }, []);

  if (projects.length === 0 && !nextEvent) return null;

  return (
    <section className="py-16 md:py-20 border-y-2 border-[#0A0A0A] bg-white relative overflow-hidden" data-testid="landing-highlights">
      <div className="absolute inset-0 halftone-pink opacity-[0.04]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {projects.length > 0 && (
          <div data-testid="currently-backing-strip">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-2">Currently backing</div>
                <h2 className="font-heading font-bold text-[#0A0A0A] text-2xl sm:text-3xl tracking-tight">Productions we&apos;re behind right now.</h2>
              </div>
              <Link to="/productions" data-testid="backing-see-all" className="inline-flex items-center gap-2 text-[#0A0A0A] font-medium border-b-2 border-[#FF2E63] pb-1 hover:text-[#FF2E63] transition-colors">
                All productions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {projects.map((p) => (
                <Link
                  to="/productions"
                  key={p.id}
                  data-testid={`backing-card-${p.id}`}
                  className="group bg-[#FFF8E7] border-2 border-[#0A0A0A] p-6 flex flex-col gap-3 hover:bg-[#FFD93D] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2E63]"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] flex items-center justify-center shrink-0"><Clapperboard className="w-4 h-4" /></span>
                    <span className="text-xs uppercase tracking-[0.14em] font-bold px-2 py-0.5 bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A]">{STATUS_LABEL[p.status] || p.status}</span>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-[#0A0A0A] leading-snug">{p.title}</h3>
                  <div className="text-sm text-[#2C2C2C]">Dir. {p.director} · {p.year}</div>
                  <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A] group-hover:text-[#FF2E63]">Get involved <ArrowRight className="w-4 h-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {nextEvent && (
          <div data-testid="next-screening-strip">
            <div className="bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow p-7 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-8">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD93D] mb-3">Next screening</div>
                <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight leading-tight">{nextEvent.title}</h3>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#FFE8A8]">
                  <span className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" /> {fmtDate(nextEvent.start_datetime)} · {fmtTime(nextEvent.start_datetime)}</span>
                  <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {nextEvent.venue}, {nextEvent.city}</span>
                </div>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link
                  to="/festival"
                  data-testid="next-screening-cta"
                  className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-7 py-4 text-sm font-medium inline-flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD93D]"
                >
                  Event details <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
